import * as React from 'react';

import {
  enumFacilitySortOption,
  enumRoleSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  Checkbox,
  Col,
  DialogContent,
  DialogFooter,
  DialogOverlay,
  DialogTitle,
  PhoneField,
  Row,
  SearchField,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { cn } from '@rematter/ui-web/lib';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck, LucideX, LucideXCircle } from 'lucide-react';

import { useCoreUserCreateMutation } from './_graphql/useCoreUserCreateMutation';
import { useFacilitiesQuery } from './_graphql/useFacilitiesQuery';
import { useRolesQuery } from './_graphql/useRolesQuery';

import {
  $addRoleAssignment,
  $addUserDialogOpen,
  $addUserDialogStep,
  $addUserFormData,
  $canProceedToRoles,
  $canSendInvite,
  $clearAssignmentsByRole,
  $closeAddUserDialog,
  $goToRoleStep,
  $goToUserInfoStep,
  $removeRoleAssignment,
  $selectedRoleAssignments,
  $updateAddUserForm,
  type AddUserDialogStep,
  type RoleAssignment,
} from '~/modules/settings/_state/addUserDialog.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';
import useDebouncedValue from '~/shared/hooks/useDebounce';

type StepIndicatorProps = {
  currentStep: AddUserDialogStep;
};

const StepIndicator = ({ currentStep }: StepIndicatorProps) => {
  const t = useTranslations();

  return (
    <Row className="@StepIndicator items-center gap-3">
      <Row className="items-center gap-1.5">
        {currentStep === 'roleAssignment' ? (
          <div className="flex size-[26px] items-center justify-center rounded bg-success p-[5px]">
            <LucideCheck className="size-4 text-abs-white" />
          </div>
        ) : (
          <div className="flex size-[26px] items-center justify-center rounded border border-success bg-success/10 px-1.5 py-0.5">
            <span className="text-sm font-medium text-success">1</span>
          </div>
        )}
        <span className="text-sm font-medium text-success">
          {t.teamAndPermissions.addUserDialog.steps.userInfo}
        </span>
      </Row>

      <div className="h-px w-4 bg-neutral/20" />

      <Row className="items-center gap-1.5">
        <div
          className={cn(
            'flex size-[26px] items-center justify-center rounded px-1.5 py-0.5 text-sm font-medium',
            currentStep === 'roleAssignment'
              ? 'border border-success bg-success/10 text-success'
              : 'border border-neutral/30 text-neutral/50',
          )}
        >
          2
        </div>
        <span
          className={cn(
            'text-sm font-medium',
            currentStep === 'roleAssignment'
              ? 'text-success'
              : 'text-neutral/50',
          )}
        >
          {t.teamAndPermissions.addUserDialog.steps.roleAssignment}
        </span>
      </Row>
    </Row>
  );
};

const UserInfoStep = () => {
  const t = useTranslations();
  const formData = useAtomValue($addUserFormData);
  const updateForm = useSetAtom($updateAddUserForm);

  return (
    <div className="@UserInfoStep flex flex-col gap-4">
      <Row gap4>
        <TextField
          aria-label={t.teamAndPermissions.addUserDialog.fields.firstName.label}
          label={t.teamAndPermissions.addUserDialog.fields.firstName.label}
          placeholder={
            t.teamAndPermissions.addUserDialog.fields.firstName.placeholder
          }
          value={formData.firstName}
          onChange={(value) => updateForm({ firstName: value })}
          isRequired
          autoFocus
          className="flex-1"
        />
        <TextField
          aria-label={t.teamAndPermissions.addUserDialog.fields.lastName.label}
          label={t.teamAndPermissions.addUserDialog.fields.lastName.label}
          placeholder={
            t.teamAndPermissions.addUserDialog.fields.lastName.placeholder
          }
          value={formData.lastName}
          onChange={(value) => updateForm({ lastName: value })}
          isRequired
          className="flex-1"
        />
      </Row>

      <TextField
        aria-label={t.teamAndPermissions.addUserDialog.fields.email.label}
        label={t.teamAndPermissions.addUserDialog.fields.email.label}
        placeholder={
          t.teamAndPermissions.addUserDialog.fields.email.placeholder
        }
        value={formData.email}
        onChange={(value) => updateForm({ email: value })}
        isRequired
        type="email"
      />

      <PhoneField
        aria-label={t.teamAndPermissions.addUserDialog.fields.phoneNumber.label}
        label={t.teamAndPermissions.addUserDialog.fields.phoneNumber.label}
        placeholder={
          t.teamAndPermissions.addUserDialog.fields.phoneNumber.placeholder
        }
        value={formData.phoneNumber}
        onChange={(value) => updateForm({ phoneNumber: value })}
        isRequired
      />
    </div>
  );
};

const RoleAssignmentStep = () => {
  const t = useTranslations();
  const [roleSearch, setRoleSearch] = React.useState('');
  const [facilitySearch, setFacilitySearch] = React.useState('');
  const debouncedRoleSearch = useDebouncedValue(roleSearch, 300);
  const debouncedFacilitySearch = useDebouncedValue(facilitySearch, 300);

  const selectedAssignments = useAtomValue($selectedRoleAssignments);
  const addAssignment = useSetAtom($addRoleAssignment);
  const removeAssignment = useSetAtom($removeRoleAssignment);
  const clearAssignmentsByRole = useSetAtom($clearAssignmentsByRole);

  const [selectedRoleId, setSelectedRoleId] = React.useState<string | null>(
    null,
  );

  const { data: { roles = [] } = {}, isLoading: rolesLoading } = useRolesQuery({
    searchTerm: debouncedRoleSearch || undefined,
    sorting: [enumRoleSortOption.NameAsc],
    limit: 1000,
    offset: 0,
  });

  const { data: { facilities = [] } = {}, isLoading: facilitiesLoading } =
    useFacilitiesQuery({
      searchTerm: debouncedFacilitySearch || undefined,
      sorting: [enumFacilitySortOption.NameAsc],
      limit: 1000,
      offset: 0,
    });

  const activeRoles = React.useMemo(
    () => roles.filter((role) => role.status !== 'archived'),
    [roles],
  );

  const isFacilitySelected = React.useCallback(
    (facilityId: string) => {
      if (!selectedRoleId) return false;
      return selectedAssignments.some(
        (a) => a.roleId === selectedRoleId && a.facilityId === facilityId,
      );
    },
    [selectedRoleId, selectedAssignments],
  );

  const toggleFacility = React.useCallback(
    (facilityId: string) => {
      if (!selectedRoleId) return;
      const assignment: RoleAssignment = {
        roleId: selectedRoleId,
        facilityId,
      };
      if (isFacilitySelected(facilityId)) {
        removeAssignment(assignment);
      } else {
        addAssignment(assignment);
      }
    },
    [selectedRoleId, isFacilitySelected, addAssignment, removeAssignment],
  );

  const roleAssignmentCount = React.useCallback(
    (roleId: string) =>
      selectedAssignments.filter((a) => a.roleId === roleId).length,
    [selectedAssignments],
  );

  return (
    <div className="@RoleAssignmentStep flex flex-1 gap-4 overflow-hidden">
      <Col className="flex-1 overflow-hidden">
        <SearchField
          value={roleSearch}
          onChange={setRoleSearch}
          placeholder={
            t.teamAndPermissions.addUserDialog.roleStep.searchRolesPlaceholder
          }
          autoFocus
        />
        <Row className="items-center pt-4 pb-0.5">
          <span className="text-sm font-semibold text-neutral/60 uppercase">
            {t.teamAndPermissions.addUserDialog.roleStep.rolesHeader} (
            {activeRoles.length})
          </span>
        </Row>
        <Col className="flex-1 gap-1 overflow-y-auto">
          {rolesLoading ? (
            <div className="py-4 text-center text-sm text-neutral/50">
              {t.teamAndPermissions.addUserDialog.roleStep.loading}
            </div>
          ) : activeRoles.length === 0 ? (
            <div className="py-4 text-center text-sm text-neutral/50">
              {t.teamAndPermissions.addUserDialog.roleStep.noRolesFound}
            </div>
          ) : (
            activeRoles.map((role) => {
              const count = roleAssignmentCount(role.id);
              return (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => setSelectedRoleId(role.id)}
                  className={cn(
                    'flex items-center justify-between rounded px-2 py-[5px] text-left hover:bg-neutral/5',
                    selectedRoleId === role.id && 'bg-primary/10',
                  )}
                >
                  <span className="text-sm leading-[22px]">{role.name}</span>
                  {count > 0 && (
                    <span className="text-xs text-primary">
                      {t.teamAndPermissions.addUserDialog.roleStep.assignmentCount(
                        count,
                      )}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </Col>
      </Col>

      <div className="w-px bg-neutral/10" />

      <Col className="flex-1 overflow-hidden">
        <SearchField
          value={facilitySearch}
          onChange={setFacilitySearch}
          placeholder={
            t.teamAndPermissions.addUserDialog.roleStep
              .searchFacilitiesPlaceholder
          }
        />
        <Row className="items-center justify-between pt-4 pb-0.5">
          <span className="text-sm font-semibold text-neutral/60 uppercase">
            {t.teamAndPermissions.addUserDialog.roleStep.facilitiesHeader} (
            {facilities.length})
          </span>
          {selectedRoleId &&
            selectedAssignments.filter((a) => a.roleId === selectedRoleId)
              .length > 0 && (
              <Button
                variant="text"
                color="primary"
                size="small"
                className="h-auto py-0 text-sm"
                icon={<LucideXCircle className="size-4" />}
                onPress={() => {
                  if (selectedRoleId) {
                    clearAssignmentsByRole(selectedRoleId);
                  }
                }}
              >
                {t.teamAndPermissions.addUserDialog.roleStep.clearButton}
              </Button>
            )}
        </Row>
        <Col className="flex-1 gap-1 overflow-y-auto">
          {!selectedRoleId ? (
            <div className="py-4 text-center text-sm text-neutral/50">
              {t.teamAndPermissions.addUserDialog.roleStep.selectRolePrompt}
            </div>
          ) : facilitiesLoading ? (
            <div className="py-4 text-center text-sm text-neutral/50">
              {t.teamAndPermissions.addUserDialog.roleStep.loading}
            </div>
          ) : facilities.length === 0 ? (
            <div className="py-4 text-center text-sm text-neutral/50">
              {t.teamAndPermissions.addUserDialog.roleStep.noFacilitiesFound}
            </div>
          ) : (
            facilities.map((facility) => (
              <Checkbox
                key={facility.id}
                isSelected={isFacilitySelected(facility.id)}
                onChange={() => toggleFacility(facility.id)}
                className="rounded py-[5px] hover:bg-neutral/5"
              >
                <Col>
                  <span className="text-sm leading-[22px]">
                    {facility.name}
                  </span>
                  <span className="text-xs leading-normal text-neutral/60">
                    {facility.address}
                  </span>
                </Col>
              </Checkbox>
            ))
          )}
        </Col>
      </Col>
    </div>
  );
};

export const AddUserDialog = () => {
  const t = useTranslations();
  const toast = useToast();
  const queryClient = useQueryClient();

  const isOpen = useAtomValue($addUserDialogOpen);
  const formData = useAtomValue($addUserFormData);
  const currentStep = useAtomValue($addUserDialogStep);
  const canProceedToRoles = useAtomValue($canProceedToRoles);
  const canSendInvite = useAtomValue($canSendInvite);
  const selectedAssignments = useAtomValue($selectedRoleAssignments);

  const closeDialog = useSetAtom($closeAddUserDialog);
  const goToRoleStep = useSetAtom($goToRoleStep);
  const goToUserInfoStep = useSetAtom($goToUserInfoStep);

  const { mutateAsync: createUser, isPending: isSending } =
    useCoreUserCreateMutation();

  const handleSendInvite = React.useCallback(async () => {
    if (!canSendInvite) return;

    try {
      await createUser({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber.trim() || null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        roleAssignments: selectedAssignments.map((a) => ({
          roleId: a.roleId,
          facilityId: a.facilityId,
        })),
      });
      toast.success(t.teamAndPermissions.addUserDialog.messages.success);
      await queryClient.invalidateQueries({
        queryKey: ['settings.teamPermissions.users'],
      });
      closeDialog();
    } catch (_error) {
      toast.error(t.teamAndPermissions.addUserDialog.messages.error);
    }
  }, [
    canSendInvite,
    formData,
    selectedAssignments,
    createUser,
    toast,
    t,
    queryClient,
    closeDialog,
  ]);

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={(open) => !open && closeDialog()}
      className="@AddUserDialog"
    >
      <DialogContent
        className={cn(
          'min-w-[500px]',
          currentStep === 'roleAssignment' &&
            'h-[600px] max-h-[80vh] min-w-[700px]',
        )}
        closeButton={false}
      >
        <div className="flex flex-col rounded-t-lg bg-[#fbfbfc]">
          <div className="flex items-center justify-between px-6 pt-5 pb-1">
            <DialogTitle className="text-xl font-bold">
              {t.teamAndPermissions.addUserDialog.title}
            </DialogTitle>
            <button
              onClick={() => closeDialog()}
              className="flex items-center justify-center rounded bg-neutral/5 p-[3px]"
              aria-label={t.teamAndPermissions.addUserDialog.actions.cancel}
            >
              <LucideX className="size-6 text-neutral/60" />
            </button>
          </div>
          <div className="border-b border-neutral/10 px-6 pt-3 pb-4">
            <StepIndicator currentStep={currentStep} />
          </div>
        </div>

        <div className="flex flex-1 flex-col overflow-hidden px-6 py-5">
          {currentStep === 'userInfo' ? (
            <UserInfoStep />
          ) : (
            <RoleAssignmentStep />
          )}
        </div>

        <DialogFooter>
          {currentStep === 'userInfo' ? (
            <>
              <Button
                aria-label={t.teamAndPermissions.addUserDialog.actions.cancel}
                variant="outline"
                color="neutral"
                onPress={() => closeDialog()}
              >
                {t.teamAndPermissions.addUserDialog.actions.cancel}
              </Button>
              <Button
                aria-label={t.teamAndPermissions.addUserDialog.actions.next}
                variant="solid"
                color="primary"
                onPress={() => goToRoleStep()}
                isDisabled={!canProceedToRoles}
              >
                {t.teamAndPermissions.addUserDialog.actions.next}
              </Button>
            </>
          ) : (
            <>
              <Button
                aria-label={t.teamAndPermissions.addUserDialog.actions.back}
                variant="outline"
                color="neutral"
                onPress={() => goToUserInfoStep()}
                isDisabled={isSending}
              >
                {t.teamAndPermissions.addUserDialog.actions.back}
              </Button>
              <Button
                aria-label={
                  t.teamAndPermissions.addUserDialog.actions.sendInvite
                }
                variant="solid"
                color="primary"
                onPress={handleSendInvite}
                isDisabled={!canSendInvite || isSending}
                isLoading={isSending}
              >
                {t.teamAndPermissions.addUserDialog.actions.sendInvite}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
